import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { GlassPanel } from '../components/common/GlassPanel';
import { Input } from '../components/common/Input';
import { Logo } from '../components/common/Logo';
import { AuthBackdrop } from '../components/auth/AuthBackdrop';

/**
 * Join page for entering a meeting room code.
 */
export default function JoinPage() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 page-shell">
      <AuthBackdrop />
      <div className="w-full max-w-lg space-y-8 relative z-10">
        <JoinHeader />
        <GlassPanel variant="strong" className="animate-fade-in-up">
          <JoinForm />
        </GlassPanel>
      </div>
    </div>
  );
}

/**
 * Header for the join page.
 */
function JoinHeader() {
  return (
    <div className="text-center space-y-3">
      <div className="flex justify-center">
        <Logo size="lg" />
      </div>
      <h1 className="text-3xl font-semibold text-gradient">Join a Room</h1>
      <p className="text-ink-200">Enter your room code to connect.</p>
    </div>
  );
}

/**
 * Join form for entering and validating the room code.
 */
function JoinForm() {
  const { roomCode, error, handleSubmit, handleBack, setRoomCode } = useJoinFormState();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Room Code"
        value={roomCode}
        onChange={(event) => setRoomCode(event.target.value.toUpperCase())}
        placeholder="ABC123"
        maxLength={6}
        autoComplete="off"
        required
      />
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      <Button type="submit" className="w-full">
        Join Room
      </Button>
      <Button type="button" variant="secondary" className="w-full" onClick={handleBack}>
        Back to Home
      </Button>
    </form>
  );
}

interface JoinFormState {
  roomCode: string;
  error: string;
  setRoomCode: (value: string) => void;
  handleSubmit: (event: FormEvent) => void;
  handleBack: () => void;
}

/**
 * Manages join form state and navigation.
 */
function useJoinFormState(): JoinFormState {
  const navigate = useNavigate();
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeRoomCode(roomCode);

    if (!normalized) {
      setError('Please enter a valid room code');
      return;
    }

    setError('');
    navigate(`/meeting/${normalized}`);
  };

  const handleBack = () => {
    navigate('/');
  };

  return { roomCode, error, setRoomCode, handleSubmit, handleBack };
}

/**
 * Normalizes the room code from user input or pasted URLs.
 */
function normalizeRoomCode(rawCode: string): string {
  const trimmed = rawCode.trim();

  if (!trimmed) {
    return '';
  }

  const segments = trimmed.split('/').filter(Boolean);
  const candidate = segments.length > 0 ? segments[segments.length - 1] : trimmed;

  return candidate.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}
